import React, { useState, useEffect } from 'react';
import { HeaderNav } from '@/components/navigation/HeaderNav';
import { HeroSection } from '@/components/landing/HeroSection';
// import { ProblemStatement } from '@/components/landing/ProblemStatement'; // HIDDEN FOR NOW
// import { ServicePreview } from '@/components/landing/ServicePreview'; // REMOVED - Services now shown in role-specific content
import { PathSelector, UserPath } from '@/components/landing/PathSelector';
import { ProgressiveContentArea } from '@/components/landing/ProgressiveContentArea';
import { GetInvolvedHub } from '@/components/landing/GetInvolvedHub';
import { BackToTopButton } from '@/components/shared/BackToTopButton';
import { useAnalytics } from '@/hooks/useAnalytics';

// Define InterestType locally to match GetInvolvedHub
type InterestType = 'GETTING_HELP' | 'VOLUNTEERING' | 'PROFESSIONAL_SERVICES';

export const LandingPage: React.FC = () => {
    const { trackUserAction } = useAnalytics();
    const [selectedPath, setSelectedPath] = useState<UserPath>(null);
    const [preselectedFormType, setPreselectedFormType] = useState<string | undefined>();

    // Check URL parameters on load to set initial path
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const pathParam = urlParams.get('path');

        if (pathParam === 'senior' || pathParam === 'volunteer' || pathParam === 'professional') {
            setSelectedPath(pathParam);
        }
    }, []);

    const handlePathSelect = (path: UserPath) => {
        setSelectedPath(path);

        // Update URL parameter
        const url = new URL(window.location.href);
        if (path) {
            url.searchParams.set('path', path);
        } else {
            url.searchParams.delete('path');
        }
        window.history.pushState({}, '', url.toString());

        // Clear any previously set form type when changing paths
        setPreselectedFormType(undefined);

        // Track path selection with Clarity
        if (path) {
            trackUserAction.heroPathSelect(path);
        }

        // Legacy analytics tracking (if analytics is available)
        if (typeof window !== 'undefined' && (window as any).gtag) {
            (window as any).gtag('event', 'path_selection', {
                event_category: 'landing_page',
                event_label: path || 'cleared',
                value: 1
            });
        }

        // Smooth scroll to dynamic content area when a path is selected
        if (path) {
            setTimeout(() => {
                const dynamicContentArea = document.querySelector('.progressive-content-area') ||
                    document.querySelector('[class*="ProgressiveContentArea"]');

                if (dynamicContentArea) {
                    const rect = dynamicContentArea.getBoundingClientRect();
                    const headerHeight = 80; // Approximate height of the fixed header
                    const scrollTop = window.pageYOffset + rect.top - headerHeight;

                    window.scrollTo({
                        top: scrollTop,
                        behavior: 'smooth'
                    });
                }
            }, 100); // Small delay to ensure content is rendered
        }
    };

    const handleFormScroll = (pathType?: string) => {
        setPreselectedFormType(pathType);

        // Analytics tracking for CTA clicks
        if (typeof window !== 'undefined' && (window as any).gtag) {
            (window as any).gtag('event', 'cta_click', {
                event_category: 'landing_page',
                event_label: `${selectedPath}_to_form`,
                value: 1
            });
        }

        // Smooth scroll to the Get Involved Hub
        const getInvolvedSection = document.querySelector('.get-involved-hub') ||
            document.querySelector('[class*="GetInvolvedHub"]');

        if (getInvolvedSection) {
            const rect = getInvolvedSection.getBoundingClientRect();
            const headerHeight = 80; // Approximate height of the fixed header
            const scrollTop = window.pageYOffset + rect.top - headerHeight;

            window.scrollTo({
                top: scrollTop,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div className="min-h-screen bg-white">
            {/* Navigation Header */}
            <HeaderNav />

            {/* Section 1: Hero - The Promise & Story */}
            <HeroSection />

            {/* Section 2: Core Action - Role Selection */}
            <PathSelector
                selectedPath={selectedPath}
                onPathSelect={handlePathSelect}
            />

            {/* Section 3: Dynamic Content Area - Shows role-specific content */}
            <ProgressiveContentArea
                selectedPath={selectedPath}
                onFormScroll={handleFormScroll}
            />

            {/* Section 5: Final Call to Action - Single Consolidated Form */}
            <GetInvolvedHub
                preselectedInterest={preselectedFormType as InterestType}
            />

            {/* Footer */}
            <footer className="bg-bibi-blue text-white py-12">
                <div className="container mx-auto px-4">
                    <div className="grid md:grid-cols-2 gap-8 lg:gap-16">
                        {/* Brand */}
                        <div>
                            <h3 className="text-2xl font-bold mb-4">BiBi</h3>
                            <p className="text-bibi-blue-light leading-relaxed mb-4">
                                Connecting seniors with reliable support through our innovative
                                network of community volunteers and professional helpers. Coming in 2026.
                            </p>
                            <div className="flex items-center gap-2 text-sage-green">
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <span className="font-medium">Reliable • Affordable • Local</span>
                            </div>
                        </div>

                        {/* Contact Info */}
                        <div>
                            <h4 className="font-semibold mb-4">Get in Touch</h4>
                            <div className="space-y-3 text-bibi-blue-light">
                                <div className="flex items-start gap-2">
                                    <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    <a
                                        href="mailto:contact@bibihelp.com"
                                        className="hover:text-white transition-colors"
                                    >
                                        contact@bibihelp.com
                                    </a>
                                </div>
                                <div className="flex items-start gap-2">
                                    <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    <span>Launching first in Wichita, 2026</span>
                                </div>

                                {/* Emergency Disclaimer */}
                                <div className="border-t border-bibi-blue-light/20 pt-3 mt-4">
                                    <p className="text-sm text-bibi-blue-light/80">
                                        <strong>Important:</strong> For emergencies, always call 911
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bottom Bar */}
                    <div className="border-t border-bibi-blue-light/20 mt-8 pt-8">
                        {/* Disclaimer */}
                        <div className="mb-6 text-center">
                            <p className="text-xs text-bibi-blue-light/80 max-w-4xl mx-auto leading-relaxed mb-3">
                                Professional services available when volunteers aren't, with transparent additional fees.
                            </p>
                            <p className="text-xs text-bibi-blue-light/80 max-w-4xl mx-auto leading-relaxed">
                                BiBi connects seniors with community volunteers and professional service providers.
                                Services are provided by independent volunteers and professionals, not BiBi employees.
                                All helpers are screened, but seniors should use their best judgment when accepting help.
                                BiBi is not responsible for the actions of individual service providers.
                            </p>
                        </div>

                        <div className="flex flex-col md:flex-row justify-center md:justify-start items-center gap-4">
                            <p className="text-bibi-blue-light text-sm">
                                © {new Date().getFullYear()} BiBi. All rights reserved.
                            </p>
                        </div>
                    </div>
                </div>
            </footer>

            {/* Back to Top Button */}
            <BackToTopButton />
        </div>
    );
};
